import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Button,
  TextInput,
  Textarea,
  NumberInput,
  Stack,
  Group,
  Text,
  Divider,
  ActionIcon,
  Select,
  Alert,
  Badge,
  Paper,
  ScrollArea,
  Grid,
  Tooltip,
  Checkbox,
} from "@mantine/core";
import {
  IconTrash,
  IconPlus,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconUserPlus,
  IconSchool,
} from "@tabler/icons-react";

import {
  CreateJourneyPayload,
  JourneyStudent,
  JourneyInstructor,
  JourneyItem,
} from "./journey.types";

import JourneyDateSlider from "./JourneyDateSlider";
import {
  generateJourneyDates,
  getMissedDates,
  formatToLocalDateISO,
} from "../utils/date.utils";
import {
  createEmptyStudent,
  createEmptyInstructor,
} from "../utils/journey.factory";
import ManageStudentsModal from "./ManageStudentsModal";
import AttendedHoursInput from "./AttendedHoursInput";
import { ApiService } from "../../api/api-service";
import {
  syncStudentsToBatch,
  getStudentsFromBatch,
  syncInstructorToBatch,
} from "../utils/batchJourneySync.utils";
import { notifications } from "@mantine/notifications";

// Moving a batch to these statuses needs a reason (shown as a tooltip on the Batches table)
export const STATUSES_REQUIRING_COMMENT = ["Hold", "Absconded", "Discarded"];

interface Props {
  opened: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateJourneyPayload) => void;
  isSubmitting: boolean;

  currentBatchData?: JourneyItem;
  editData?: JourneyItem;

  selectedDate: Date | null;
  existingJourneys: JourneyItem[];
  onDateChange: (date: Date) => void;

  studentsDropdown?: { label: string; value: string; name: string }[];
  instructorsDropdown?: { label: string; value: string; name: string }[];

  batchNo: string;
  onCreateStudent: () => void;
  initialBatchData?: any | null;
}

const formatDate = (d: Date | null) =>
  d
    ? d.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const AddJourneyModal = ({
  opened,
  onClose,
  onSubmit,
  isSubmitting,
  currentBatchData,
  editData,
  selectedDate,
  existingJourneys,
  onDateChange,
  studentsDropdown = [],
  instructorsDropdown = [],
  batchNo,
  onCreateStudent,
  initialBatchData,
}: Props) => {
  const [changeSummary, setChangeSummary] = useState("");
  const [numSessions, setNumSessions] = useState(0);
  const [batchStatus, setBatchStatus] = useState("On-going"); //  REQUIREMENT #11: Default to On-going

  // Issue tracking state
  const [isIssue, setIsIssue] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");

  const [students, setStudents] = useState<JourneyStudent[]>([]);
  const [instructors, setInstructors] = useState<JourneyInstructor[]>([]);

  const [manageStudentsOpen, setManageStudentsOpen] = useState(false);

  //  REQUIREMENT #9: Get last 3 journey dates
  const lastThreeJourneys = useMemo(() => {
    if (existingJourneys.length === 0) return [];
    return existingJourneys.slice(0, 3);
  }, [existingJourneys]);

  // Check if editing existing journey for this date
  const existingJourneyForDate = useMemo(() => {
    if (!selectedDate) return null;
    const dateStr = formatToLocalDateISO(selectedDate);
    return existingJourneys.find((j) => j.journeyDate === dateStr);
  }, [selectedDate, existingJourneys]);

  const originalTargetDateRef = useRef<Date | null>(null);

  useEffect(() => {
    if (opened && selectedDate && !originalTargetDateRef.current) {
      originalTargetDateRef.current = new Date(selectedDate);
    }
    if (!opened) {
      originalTargetDateRef.current = null;
    }
  }, [opened, selectedDate]);

  const missedDates = useMemo(() => {
    const targetDate = originalTargetDateRef.current || selectedDate;
    if (!targetDate) return [];
    return getMissedDates(existingJourneys, targetDate);
  }, [existingJourneys, opened]);

  const hasMissedDates = missedDates.length > 0;

  const currentDateHasMissedDates = useMemo(() => {
    if (!selectedDate) return false;
    return getMissedDates(existingJourneys, selectedDate).length > 0;
  }, [selectedDate, existingJourneys]);

  const formatDate = (d: Date | null) =>
    d
      ? d.toLocaleDateString("en-US", {
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";

  //  FIXED: Use batch trainerEmail to identify current trainer (not array position)
  //  After merge, instructor array order is NOT guaranteed — the last element may not be the current trainer.
  const isCurrentTrainer = (index: number): boolean => {
    const currentTrainerEmail = initialBatchData?.trainerEmail || "";
    if (currentTrainerEmail && instructors[index]?.email) {
      return (
        instructors[index].email.toLowerCase() ===
        currentTrainerEmail.toLowerCase()
      );
    }
    // Fallback: last instructor (for batches without trainerEmail set)
    return index === instructors.length - 1;
  };
  //  REQUIREMENT #4: Get current batch trainer email for greying out
  // const currentBatchTrainerEmail = useMemo(() => {
  //   return (
  //     initialBatchData?.trainerEmail || currentBatchData?.trainerEmail || ""
  //   );
  // }, [initialBatchData, currentBatchData]);

  // Load data when modal opens or date changes
  // useEffect(() => {
  //   const loadData = async () => {
  //     if (!opened) return;

  //     // PRIORITY 1: Editing existing journey
  //     if (existingJourneyForDate) {
  //       console.log(
  //         "Loading existing journey for date:",
  //         existingJourneyForDate.journeyDate,
  //       );

  //       setChangeSummary(existingJourneyForDate.changeSummary || "");
  //       setNumSessions(existingJourneyForDate.numSessions || 0);
  //       setBatchStatus(existingJourneyForDate.batchStatus || "On-going");

  //       setStudents(
  //         (existingJourneyForDate.students || []).map((s: any) => ({
  //           ...s,
  //           attendance: s.attendance || "Present",
  //         })),
  //       );

  //       setInstructors(
  //         (existingJourneyForDate.instructors || []).map((i: any) => {
  //           let classes = i.instructorClasses || "0";
  //           if (classes === "0" || classes === "") {
  //             const batchInstructorsList =
  //               initialBatchData?.instructors || initialBatchData?.instructor;
  //             if (batchInstructorsList) {
  //               const batchInst = batchInstructorsList.find(
  //                 (b: any) => b.email === i.email,
  //               );
  //               if (batchInst && batchInst.attendedHours) {
  //                 classes = batchInst.attendedHours;
  //               }
  //             }
  //           }
  //           return {
  //             ...i,
  //             instructorClasses: classes,
  //             attendance: i.attendance || "Present",
  //           };
  //         }),
  //       );
  //       return;
  //     }

  //     // PRIORITY 2: New entry -  REQUIREMENT #2: "No" case should not reset values to zero
  //     try {
  //       let batchStudents = [];
  //       if (initialBatchData?.students) {
  //         batchStudents = initialBatchData.students.map((s: any) => ({
  //           ...createEmptyStudent(),
  //           name: s.name,
  //           email: s.email,
  //           studentClasses: s.studentClasses || "",
  //           attendance: "Present",
  //         }));
  //       } else {
  //         batchStudents = await getStudentsFromBatch(batchNo);
  //       }

  //       setChangeSummary("");

  //       //  REQUIREMENT #2: Use last journey's numSessions if exists, else batch numSessions
  //       const lastJourney =
  //         existingJourneys.length > 0 ? existingJourneys[0] : null;
  //       setNumSessions(
  //         lastJourney
  //           ? lastJourney.numSessions
  //           : initialBatchData?.numSessions || 0,
  //       );

  //       setBatchStatus("On-going"); //  Default to On-going
  //       setStudents(batchStudents);

  //       //  REQUIREMENT #2: Pre-fill from last journey, don't reset to zero
  //       if (lastJourney?.instructors && lastJourney.instructors.length > 0) {
  //         const prevInstructors = lastJourney.instructors.map((i: any) => {
  //           // Use last journey's instructorClasses, not batch attendedHours
  //           return {
  //             ...i,
  //             attendance: "Present",
  //             instructorClasses: i.instructorClasses || "0", //  Preserve last value
  //           };
  //         });
  //         setInstructors(prevInstructors);
  //       } else if (
  //         initialBatchData?.instructors ||
  //         initialBatchData?.instructor
  //       ) {
  //         const list =
  //           initialBatchData?.instructors || initialBatchData?.instructor;
  //         const batchInstructors = list.map((i: any) => ({
  //           ...createEmptyInstructor(),
  //           name: i.name || "",
  //           email: i.email || "",
  //           instructorClasses: i.attendedHours || "0",
  //           attendance: "Present",
  //         }));
  //         setInstructors(batchInstructors);
  //       } else {
  //         setInstructors([createEmptyInstructor()]);
  //       }
  //     } catch (error) {
  //       console.error("Error loading batch data:", error);
  //       notifications.show({
  //         title: "Error",
  //         message: "Failed to load batch data",
  //         color: "red",
  //       });
  //     }
  //   };

  //   loadData();
  // }, [
  //   opened,
  //   selectedDate,
  //   existingJourneyForDate,
  //   batchNo,
  //   initialBatchData,
  //   existingJourneys,
  // ]);

  // useEffect(() => {
  //   const loadData = async () => {
  //     if (!opened) return;

  //     // PRIORITY 1: Editing existing journey
  //     if (existingJourneyForDate) {
  //       setChangeSummary(existingJourneyForDate.changeSummary || "");
  //       setNumSessions(existingJourneyForDate.numSessions || 0);
  //       setBatchStatus(existingJourneyForDate.batchStatus || "On-going");

  //       setStudents(
  //         (existingJourneyForDate.students || []).map((s: any) => ({
  //           ...s,
  //           attendance: s.attendance || "Present",
  //         })),
  //       );

  //       //  Load instructors from existing journey
  //       setInstructors(
  //         (existingJourneyForDate.instructors || []).map((i: any) => ({
  //           ...i,
  //           instructorClasses: i.instructorClasses || "0",
  //           attendance: i.attendance || "Present",
  //         })),
  //       );
  //       return;
  //     }

  //     // PRIORITY 2: New entry - Load from batch or last journey
  //     try {
  //       // Load students
  //       let batchStudents = [];
  //       if (initialBatchData?.students) {
  //         batchStudents = initialBatchData.students.map((s: any) => ({
  //           ...createEmptyStudent(),
  //           name: s.name,
  //           email: s.email,
  //           studentClasses: s.studentClasses || "",
  //           attendance: "Present",
  //         }));
  //       } else {
  //         batchStudents = await getStudentsFromBatch(batchNo);
  //       }

  //       setChangeSummary("");

  //       const lastJourney =
  //         existingJourneys.length > 0 ? existingJourneys[0] : null;
  //       setNumSessions(
  //         lastJourney
  //           ? lastJourney.numSessions
  //           : initialBatchData?.numSessions || 0,
  //       );
  //       setBatchStatus("On-going");
  //       setStudents(batchStudents);

  //       //  CRITICAL FIX: Load instructors with proper mapping

  //       if (lastJourney?.instructors && lastJourney.instructors.length > 0) {
  //         //  OPTION A: Load from last journey (preserve values)
  //         const prevInstructors = lastJourney.instructors.map((i: any) => ({
  //           ...i,
  //           attendance: "Present",
  //           instructorClasses: i.instructorClasses || "0",
  //         }));
  //         setInstructors(prevInstructors);
  //       } else if (
  //         initialBatchData?.instructors ||
  //         initialBatchData?.instructor
  //       ) {
  //         //  OPTION B: Load from batch data
  //         const list =
  //           initialBatchData?.instructors || initialBatchData?.instructor;

  //         const batchInstructors = list.map((i: any) => {
  //           return {
  //             ...createEmptyInstructor(),
  //             name: i.name || "",
  //             email: i.email || "",
  //             //  CRITICAL: Map attendedHours to instructorClasses
  //             instructorClasses: i.attendedHours || "0",
  //             attendance: "Present",
  //           };
  //         });

  //         setInstructors(batchInstructors);
  //       } else {
  //         // Fallback: empty instructor

  //         setInstructors([createEmptyInstructor()]);
  //       }
  //     } catch (error) {
  //       console.error(" Error loading batch data:", error);
  //       notifications.show({
  //         title: "Error",
  //         message: "Failed to load batch data",
  //         color: "red",
  //       });
  //     }
  //   };

  //   loadData();
  // }, [
  //   opened,
  //   selectedDate,
  //   existingJourneyForDate,
  //   batchNo,
  //   initialBatchData,
  //   existingJourneys,
  // ]);
  useEffect(() => {
    const loadData = async () => {
      if (!opened) return;

      // PRIORITY 1: Editing existing journey
      if (existingJourneyForDate) {
        // console.log(
        //   " Loading existing journey for date:",
        //   existingJourneyForDate.journeyDate,
        // );

        setChangeSummary(existingJourneyForDate.changeSummary || "");
        setNumSessions(existingJourneyForDate.numSessions || 0);
        setBatchStatus(existingJourneyForDate.batchStatus || "On-going");

        // Restore issue tracking fields so re-saves don't wipe them
        setIsIssue(existingJourneyForDate.is_issue ?? false);
        setIssueDescription(existingJourneyForDate.issue_description || "");

        setStudents(
          (existingJourneyForDate.students || []).map((s: any) => ({
            ...s,
            attendance: s.attendance || "Present",
          })),
        );

        //  FIX: Load instructors from existing journey
        setInstructors(
          (existingJourneyForDate.instructors || []).map((i: any) => ({
            ...i,
            instructorClasses: i.instructorClasses || "0",
            attendance: i.attendance || "Present",
          })),
        );
        return;
      }

      // PRIORITY 2: New entry - Load latest batch data
      try {
        const lastJourney =
          existingJourneys.length > 0 ? existingJourneys[0] : null;

        //  Build a lookup of each student's last-saved status (by email) so
        //  it carries forward to the new journey date instead of resetting.
        //  Status lives on the journey record, not the Batch Table, so the
        //  previous journey is the source of truth here.
        const lastStatusByEmail = new Map<string, string>();
        (lastJourney?.students || []).forEach((s: any) => {
          if (s.email && s.studentStatus) {
            lastStatusByEmail.set(s.email.toLowerCase(), s.studentStatus);
          }
        });

        // Load students
        let batchStudents = [];
        if (initialBatchData?.students) {
          batchStudents = initialBatchData.students.map((s: any) => ({
            ...createEmptyStudent(),
            name: s.name,
            email: s.email,
            studentClasses: s.studentClasses || "",
            attendance: "Present",
            studentStatus:
              lastStatusByEmail.get((s.email || "").toLowerCase()) ||
              s.studentStatus ||
              "Active",
          }));
        } else {
          batchStudents = (await getStudentsFromBatch(batchNo)).map(
            (s: any) => ({
              ...s,
              studentStatus:
                lastStatusByEmail.get((s.email || "").toLowerCase()) ||
                s.studentStatus ||
                "Active",
            }),
          );
        }

        setChangeSummary("");

        setNumSessions(
          lastJourney
            ? lastJourney.numSessions
            : initialBatchData?.numSessions || 0,
        );

        setBatchStatus("On-going");
        setStudents(batchStudents);

        //  CRITICAL FIX: Always load fresh instructor data from batch
        // console.log(" Loading instructor data...");

        // Get the batch instructor list
        const batchInstructorList =
          initialBatchData?.instructors || initialBatchData?.instructor || [];

        // console.log(
        //   " Batch has",
        //   batchInstructorList.length,
        //   "instructor(s)",
        // );

        if (batchInstructorList.length > 0) {
          //  Map ALL instructors from batch to journey format
          const freshInstructors = batchInstructorList.map((batchInst: any) => {
            // console.log(
            //   `  ✓ Mapping instructor: ${batchInst.name} (${batchInst.email})`,
            // );
            // console.log(
            //   `    attendedHours from batch: ${batchInst.attendedHours}`,
            // );

            return {
              name: batchInst.name || "",
              email: batchInst.email || "",
              // Attended Hours UI is hidden (managed by LMS webhook), but we still
              // snapshot the value so journey history stays consistent with older entries.
              instructorClasses: batchInst.attendedHours || "0",
              attendance: "Present",
            };
          });

          // console.log(
          //   " Setting",
          //   freshInstructors.length,
          //   "instructor(s) to journey",
          // );
          setInstructors(freshInstructors);
        } else {
          // Fallback: empty instructor
          // console.log(" No instructors found in batch, using empty");
          setInstructors([createEmptyInstructor()]);
        }
      } catch (error) {
        console.error(" Error loading batch data:", error);
        notifications.show({
          title: "Error",
          message: "Failed to load batch data",
          color: "red",
        });
      }
    };

    loadData();
  }, [
    opened,
    selectedDate,
    existingJourneyForDate,
    batchNo,
    initialBatchData,
    existingJourneys,
  ]);
  const handleStudentsChange = async (updatedStudents: JourneyStudent[]) => {
    try {
      await syncStudentsToBatch(batchNo, updatedStudents);
      setStudents(updatedStudents);

      notifications.show({
        title: "Success",
        message: "Students updated and synced with Batch Table",
        color: "green",
        icon: <IconCheck size={16} />,
      });

      setManageStudentsOpen(false);
    } catch (error) {
      console.error("Error updating students:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update students",
        color: "red",
        icon: <IconX size={16} />,
      });
    }
  };

  const addInstructor = () =>
    setInstructors((p) => [...p, createEmptyInstructor()]);

  const removeInstructor = (index: number) => {
    if (instructors.length === 1) {
      notifications.show({
        title: "Error",
        message: "At least one instructor is required",
        color: "red",
      });
      return;
    }
    setInstructors((p) => p.filter((_, i) => i !== index));
  };

  const updateInstructor = (
    index: number,
    field: keyof JourneyInstructor,
    value: any,
  ) => {
    setInstructors((p) => {
      const u = [...p];
      u[index] = { ...u[index], [field]: value };
      return u;
    });
  };

  // const handleInstructorEmailChange = (index: number, email: string | null) => {
  //   if (!email) return;
  //   const i = instructorsDropdown.find((x) => x.value === email);
  //   if (!i) return;
  //   setInstructors((p) => {
  //     const u = [...p];
  //     const batchList =
  //       initialBatchData?.instructors || initialBatchData?.instructor;
  //     const bInst = batchList?.find((b: any) => b.email === email);

  //     u[index] = {
  //       ...u[index],
  //       email,
  //       name: i.name,
  //       instructorClasses:
  //         bInst?.attendedHours || u[index].instructorClasses || "0",
  //     };
  //     return u;
  //   });
  // };

  // Submit handler
  // const handleInstructorEmailChange = (index: number, email: string | null) => {
  //   if (!email) return;

  //   const instructorOption = instructorsDropdown.find((x) => x.value === email);
  //   if (!instructorOption) return;

  //   setInstructors((prev) => {
  //     const updated = [...prev];

  //     // Find this instructor in batch data
  //     const batchList =
  //       initialBatchData?.instructors || initialBatchData?.instructor;
  //     const batchInstructor = batchList?.find((b: any) => b.email === email);

  //     //  CRITICAL: Map attendedHours to instructorClasses
  //     const instructorClasses = batchInstructor?.attendedHours || "0";

  //     updated[index] = {
  //       ...updated[index],
  //       email,
  //       name: instructorOption.name,
  //       instructorClasses: instructorClasses, //  FIX: Use batch attendedHours
  //     };

  //     return updated;
  //   });
  // };
  const handleInstructorEmailChange = (index: number, email: string | null) => {
    if (!email) return;

    const instructorOption = instructorsDropdown.find((x) => x.value === email);
    if (!instructorOption) return;

    // console.log(" Instructor email changed to:", email);

    setInstructors((prev) => {
      const updated = [...prev];

      //  Find this instructor in the LATEST batch data
      const batchList =
        initialBatchData?.instructors || initialBatchData?.instructor;
      const batchInstructor = batchList?.find((b: any) => b.email === email);

      // console.log(" Found in batch:", batchInstructor?.name);
      // console.log("Batch attendedHours:", batchInstructor?.attendedHours);

      // Attended Hours UI is hidden (managed by LMS webhook), but we still
      // snapshot the value so journey history stays consistent with older entries.
      const instructorClasses = batchInstructor?.attendedHours || "0";

      // console.log(" Setting instructorClasses to:", instructorClasses);

      updated[index] = {
        ...updated[index],
        email,
        name: instructorOption.name,
        instructorClasses: instructorClasses,
      };

      return updated;
    });
  };
  // Instructors whose Attended Hours the admin edited in this modal. Only
  // these are written to the batch (with an audit log) so an untouched,
  // possibly stale journey snapshot never overwrites newer batch hours.
  const editedHoursEmails = useRef<Set<string>>(new Set());

  const saveEditedAttendedHours = async () => {
    const edited = instructors.filter((i) =>
      editedHoursEmails.current.has((i.email || "").toLowerCase()),
    );
    for (const ins of edited) {
      const hours = Number(ins.instructorClasses);
      if (!Number.isFinite(hours) || hours < 0) continue;
      await ApiService.getInstance().put(
        "/batches/manual-update-attended-hours",
        { batch_no: batchNo, trainerEmail: ins.email, attendedHours: hours },
      );
    }
    editedHoursEmails.current.clear();
  };

  const summaryRequired = STATUSES_REQUIRING_COMMENT.includes(batchStatus);

  const handleSubmit = async () => {
    if (currentDateHasMissedDates) return;

    if (summaryRequired && !changeSummary.trim()) {
      notifications.show({
        title: "Change Summary required",
        message: `Please fill the Change Summary before saving the batch as ${batchStatus}.`,
        color: "red",
      });
      return;
    }

    if (instructors.length === 0 || !instructors[0].email) {
      notifications.show({
        title: "Error",
        message: "At least one instructor with email is required",
        color: "red",
      });
      return;
    }

    //  FIXED: Find current trainer by batch trainerEmail, not array position
    const currentTrainerEmail = initialBatchData?.trainerEmail || "";
    const currentTrainer = currentTrainerEmail
      ? instructors.find(
          (i) => i.email.toLowerCase() === currentTrainerEmail.toLowerCase(),
        ) || instructors[instructors.length - 1]
      : instructors[instructors.length - 1];

    // Previous trainers are no longer on the batch: mark them "Changed"
    const journeyInstructors: JourneyInstructor[] = instructors.map(
      (ins, idx) =>
        isCurrentTrainer(idx) ? ins : { ...ins, attendance: "Changed" },
    );

    try {
      await saveEditedAttendedHours();
      await syncInstructorToBatch(batchNo, instructors);

      const payload: CreateJourneyPayload = {
        changeSummary,
        createdBy: "admin",
        numSessions,
        trainerName: currentTrainer.name,
        trainerEmail: currentTrainer.email,
        batchStatus,
        students,
        instructors: journeyInstructors,
        is_marked: true,
        is_issue: isIssue,
        issue_description: isIssue ? issueDescription : undefined,
      };

      onSubmit(payload);
    } catch (error) {
      console.error("Error syncing instructor to batch:", error);
      notifications.show({
        title: "Error",
        message: "Failed to sync instructor to batch",
        color: "red",
      });
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        size="xl"
        title={
          <div className="w-full space-y-2">
            <div className="flex items-center gap-3">
              <Text className="text-lg font-bold text-[#221E45]">
                {existingJourneyForDate ? "Edit Journey" : "Mark Journey"}
              </Text>
              {selectedDate && (
                <Badge
                  size="sm"
                  className="bg-[#FC7A69]/15 text-[#FC7A69] font-semibold"
                >
                  {formatDate(selectedDate)}
                </Badge>
              )}
              {existingJourneyForDate && (
                <Badge size="sm" color="blue">
                  Editing Existing
                </Badge>
              )}
            </div>

            {/*  REQUIREMENT #1 & #9: Show last 3 days */}
            {/* {selectedDate && lastThreeJourneys.length > 0 && (
              <div className="w-full">
                <Text size="xs" className="text-[#221E45]/60 mb-2">
                  Recent Journey Entries:
                </Text>
                <div className="flex gap-2 flex-wrap">
                  {lastThreeJourneys.map((journey, idx) => (
                    <Badge
                      key={journey.journey_id}
                      size="sm"
                      className={
                        idx === 0
                          ? "bg-[#FC7A69]/20 text-[#FC7A69]"
                          : "bg-[#221E45]/10 text-[#221E45]"
                      }
                    >
                      {new Date(journey.journeyDate).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                        },
                      )}
                      {" - "}
                      {journey.batchStatus}
                    </Badge>
                  ))}
                </div>
              </div>
            )} */}

            {/* {selectedDate && (
              <div className="w-full">
                <JourneyDateSlider
                  dates={generateJourneyDates(existingJourneys, selectedDate)}
                  selectedDate={selectedDate}
                  onSelect={onDateChange}
                />
              </div>
            )} */}
          </div>
        }
        styles={{
          header: {
            borderBottom: "2px solid #FC7A69",
            paddingBottom: "1rem",
          },
          title: {
            width: "100%",
          },
        }}
      >
        <ScrollArea h="70vh">
          <Stack gap="md">
            {hasMissedDates && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title={
                  <Text className="text-sm font-bold text-[#221E45]">
                    Missed Dates Detected
                  </Text>
                }
                color="yellow"
                className="border-l-4 border-[#FC7A69] bg-[#FC7A69]/5"
              >
                <Text size="xs" className="text-[#221E45]/80 mb-2">
                  You have {missedDates.length} missed date(s). Click to jump:
                </Text>
                <div className="flex flex-wrap gap-1">
                  {missedDates.map((date) => (
                    <Badge
                      key={date.toISOString()}
                      size="xs"
                      className="bg-[#221E45]/10 text-[#221E45] cursor-pointer hover:bg-[#221E45]/20"
                      onClick={() => onDateChange(date)}
                    >
                      {date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </Badge>
                  ))}
                </div>
              </Alert>
            )}

            <Textarea
              label={
                <Text className="text-xs font-semibold text-[#221E45]">
                  Change Summary
                </Text>
              }
              placeholder="Describe what happened today…"
              description={
                summaryRequired
                  ? `Mandatory for ${batchStatus}. It is shown as a tooltip on the batch status.`
                  : undefined
              }
              error={
                summaryRequired && !changeSummary.trim()
                  ? "Change Summary is required for this status"
                  : undefined
              }
              value={changeSummary}
              onChange={(e) => setChangeSummary(e.currentTarget.value)}
              required
              minRows={3}
              styles={{
                input: {
                  fontSize: "0.75rem",
                  borderColor: "#FC7A69",
                  "&:focus": { borderColor: "#FC7A69" },
                },
              }}
            />
            {/* Issue Tracking Section */}
            <Divider className="border-[#FC7A69]/30" />
            <Paper
              p="md"
              withBorder
              radius="md"
              className={
                isIssue
                  ? "bg-red-50/50 border-red-300"
                  : "bg-gray-50/50 border-gray-200"
              }
            >
              <Checkbox
                label={
                  <Text
                    size="sm"
                    fw={600}
                    className={isIssue ? "text-red-700" : "text-[#221E45]"}
                  >
                    ⚠️ Mark as Issue
                  </Text>
                }
                checked={isIssue}
                onChange={(e) => setIsIssue(e.currentTarget.checked)}
                color="red"
                styles={{
                  input: { cursor: "pointer" },
                }}
              />
              {isIssue && (
                <Textarea
                  mt="sm"
                  label={
                    <Text className="text-xs font-semibold text-red-700">
                      Issue Description
                    </Text>
                  }
                  placeholder="Describe the issue..."
                  value={issueDescription}
                  onChange={(e) => setIssueDescription(e.currentTarget.value)}
                  minRows={2}
                  styles={{
                    input: {
                      fontSize: "0.75rem",
                      borderColor: "#ef4444",
                      "&:focus": { borderColor: "#ef4444" },
                    },
                  }}
                />
              )}
            </Paper>

            <Group grow>
              <NumberInput
                label={
                  <Text className="text-xs font-semibold text-[#221E45]">
                    Sessions
                  </Text>
                }
                value={numSessions}
                onChange={(val) => setNumSessions(Number(val))}
                min={0}
                styles={{
                  input: { fontSize: "0.75rem", borderColor: "#FC7A69" },
                }}
              />
              <Select
                label={
                  <Text className="text-xs font-semibold text-[#221E45]">
                    Status
                  </Text>
                }
                value={batchStatus}
                onChange={(val) => {
                  setBatchStatus(val || "On-going");
                  if (val && STATUSES_REQUIRING_COMMENT.includes(val)) {
                    notifications.show({
                      title: "Change Summary is mandatory",
                      message: `Fill the Change Summary to save the batch as ${val}. It will be shown as a tooltip on the batch status.`,
                      color: "orange",
                    });
                  }
                }}
                data={[
                  "Not Started",
                  "Not Schedule",
                  "On-going",
                  "Completed",
                  "Hold",
                  "Absconded",
                  "Discarded",
                  // "Leave",
                  // "Holiday",
                ]}
                styles={{
                  input: { fontSize: "0.75rem", borderColor: "#FC7A69" },
                }}
              />
            </Group>

            <Divider className="border-[#FC7A69]/30" />

            {/* Students section */}
            <Paper
              p="md"
              withBorder
              radius="md"
              className="bg-blue-50/50 border-blue-200"
            >
              <Group justify="space-between" mb="sm">
                <div className="flex items-center gap-2">
                  <IconUserPlus size={18} className="text-blue-600" />
                  <Text className="text-sm font-bold text-blue-900">
                    Students
                  </Text>
                  <Badge size="xs" className="bg-blue-100 text-blue-700">
                    {students.length}
                  </Badge>
                </div>
                <Button
                  size="xs"
                  variant="outline"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setManageStudentsOpen(true)}
                  className="border-blue-500 text-blue-600 hover:bg-blue-50"
                >
                  Manage Students
                </Button>
              </Group>

              {students.length > 0 ? (
                <Stack gap="xs">
                  {students.slice(0, 3).map((student, idx) => (
                    <Paper key={idx} p="xs" withBorder>
                      <Group justify="space-between">
                        <div>
                          <Text size="xs" fw={600}>
                            {student.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {student.email}
                          </Text>
                        </div>
                        <Badge
                          size="xs"
                          color={
                            (student as any).studentStatus === "Hold"
                              ? "yellow"
                              : (student as any).studentStatus === "Absconded"
                                ? "red"
                                : "green"
                          }
                        >
                          {(student as any).studentStatus || "Active"}
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                  {students.length > 3 && (
                    <Text size="xs" c="dimmed" ta="center">
                      +{students.length - 3} more students
                    </Text>
                  )}
                </Stack>
              ) : (
                <Text size="xs" c="dimmed" ta="center">
                  No students yet. Click "Manage Students" to add.
                </Text>
              )}
            </Paper>

            <Divider className="border-[#FC7A69]/30" />

            {/* Instructors section */}
            <Group justify="space-between">
              <div className="flex items-center gap-2">
                <IconSchool size={18} className="text-[#221E45]" />
                <Text className="text-sm font-bold text-[#221E45]">
                  Instructors
                </Text>
                <Badge size="xs" className="bg-[#221E45]/10 text-[#221E45]">
                  {instructors.length}
                </Badge>
              </div>
              {/* <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addInstructor}
                className="bg-[#221E45] hover:bg-[#221E45]/90 text-white"
                radius="md"
              >
                Add Instructor
              </Button> */}
            </Group>

            <Alert
              icon={<IconAlertCircle size={16} />}
              color="blue"
              className="border-l-4 border-blue-500"
            >
              <Text size="xs" className="text-[#221E45]/80">
                💡 The <strong>current trainer</strong> is identified by the
                batch's trainer email and is highlighted with a green badge
                below.
              </Text>
            </Alert>

            {/* {instructors.map((instructor, index) => {
              return (
                <Paper key={index} p="sm" withBorder radius="md">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text fw={600} size="sm" className="text-[#221E45]">
                        Instructor {index + 1}
                      </Text>
                      {index === instructors.length - 1 && (
                        <Badge size="xs" color="green">
                          Current Trainer
                        </Badge>
                      )}
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeInstructor(index)}
                      disabled={instructors.length === 1}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <Grid>
                    <Grid.Col span={6}>
                      <Select
                        label="Instructor Email"
                        data={instructorsDropdown}
                        value={instructor.email}
                        onChange={(email) =>
                          handleInstructorEmailChange(index, email)
                        }
                        searchable
                        required
                        // disabled={isDisabled}
                        styles={{
                          input: { fontSize: "0.75rem" },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Instructor Name"
                        value={instructor.name}
                        readOnly
                        // disabled={isDisabled}
                        styles={{
                          input: {
                            fontSize: "0.75rem",
                            backgroundColor: "#f5f5f5",
                          },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Attended Hours"
                        value={instructor.instructorClasses}
                        onChange={(e) =>
                          updateInstructor(
                            index,
                            "instructorClasses",
                            e.target.value,
                          )
                        }
                        // disabled={isDisabled}
                        styles={{
                          input: { fontSize: "0.75rem" },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <Select
                        label="Attendance"
                        data={["Present", "Not Schedule", "Leave", "Holiday"]}
                        value={instructor.attendance}
                        onChange={(v) =>
                          updateInstructor(index, "attendance", v || "Present")
                        }
                        // disabled={isDisabled}
                        styles={{
                          input: { fontSize: "0.75rem" },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                  </Grid>
                </Paper>
              );
            })} */}
            {instructors.map((instructor, index) => {
              //  Check if this is the current active trainer
              const isCurrent = isCurrentTrainer(index);

              return (
                <Paper key={index} p="sm" withBorder radius="md">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text fw={600} size="sm" className="text-[#221E45]">
                        Instructor {index + 1}
                      </Text>
                      {/* Only the LAST instructor is the current trainer */}
                      {isCurrentTrainer(index) ? (
                        <Badge size="xs" color="green">
                          CURRENT TRAINER
                        </Badge>
                      ) : (
                        <Badge size="xs" color="gray" variant="light">
                          PREVIOUS TRAINER
                        </Badge>
                      )}
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeInstructor(index)}
                      disabled={instructors.length === 1}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                  <Grid>
                    <Grid.Col span={6}>
                      <Select
                        label="Instructor Email"
                        data={instructorsDropdown}
                        value={instructor.email}
                        onChange={(email) =>
                          handleInstructorEmailChange(index, email)
                        }
                        searchable
                        required
                        styles={{
                          input: { fontSize: "0.75rem" },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <TextInput
                        label="Instructor Name"
                        value={instructor.name}
                        readOnly
                        styles={{
                          input: {
                            fontSize: "0.75rem",
                            backgroundColor: "#f5f5f5",
                          },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      <AttendedHoursInput
                        value={instructor.instructorClasses}
                        onChange={(hours) => {
                          editedHoursEmails.current.add(
                            (instructor.email || "").toLowerCase(),
                          );
                          updateInstructor(index, "instructorClasses", hours);
                        }}
                      />
                    </Grid.Col>
                    <Grid.Col span={6}>
                      {/*  CRITICAL FIX: Only allow attendance change for current trainer */}
                      <Select
                        label={
                          <Group gap={4}>
                            <Text size="xs" fw={600}>
                              Attendance
                            </Text>
                            {!isCurrent && (
                              <Tooltip label="Only the active instructor can have attendance managed">
                                <IconAlertCircle
                                  size={12}
                                  className="text-gray-400"
                                />
                              </Tooltip>
                            )}
                          </Group>
                        }
                        data={["Present", "Not Schedule", "Leave", "Holiday", "Changed"]}
                        value={isCurrent ? instructor.attendance : "Changed"}
                        onChange={(v) =>
                          updateInstructor(index, "attendance", v || "Present")
                        }
                        //  Disable attendance for non-current instructors
                        disabled={!isCurrentTrainer(index)}
                        styles={{
                          input: {
                            fontSize: "0.75rem",
                            backgroundColor: !isCurrent ? "#f5f5f5" : "white",
                            cursor: !isCurrent ? "not-allowed" : "pointer",
                          },
                          label: { fontSize: "0.7rem", fontWeight: 600 },
                        }}
                      />
                    </Grid.Col>
                  </Grid>

                  {/*  NEW: Warning message for previous trainers */}
                  {!isCurrent && instructor.email && (
                    <Alert
                      icon={<IconAlertCircle size={14} />}
                      color="gray"
                      variant="light"
                      mt="xs"
                      styles={{
                        message: { fontSize: "0.65rem" },
                      }}
                    >
                      This instructor is not the current active trainer.
                      Attendance cannot be modified.
                    </Alert>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </ScrollArea>

        <Group
          justify="flex-end"
          mt="md"
          className="pt-3 border-t-2 border-[#FC7A69]/30"
        >
          <Button
            variant="subtle"
            onClick={onClose}
            className="text-[#221E45] hover:bg-[#221E45]/10"
            size="sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            className="bg-[#FC7A69] hover:bg-[#FC7A69]/90 text-white"
            disabled={currentDateHasMissedDates}
            size="sm"
            leftSection={
              currentDateHasMissedDates ? (
                <IconX size={14} />
              ) : (
                <IconCheck size={14} />
              )
            }
          >
            {currentDateHasMissedDates
              ? "Fill Missed Dates First"
              : existingJourneyForDate
                ? "Update Entry"
                : "Save Journey"}
          </Button>
        </Group>
      </Modal>

      <ManageStudentsModal
        opened={manageStudentsOpen}
        onClose={() => setManageStudentsOpen(false)}
        currentStudents={students}
        studentsDropdown={studentsDropdown}
        onSave={handleStudentsChange}
        isSubmitting={false}
        onCreateStudent={onCreateStudent}
      />
    </>
  );
};

export default AddJourneyModal;
